/**
 * Onboarding feature module (W2-14 onboarding-wizard; DESIGN.md §4.2, §5.14; WORKPACKAGES Appendix B: `help.*`
 * except `help.palette` / `help.cheatSheet`, `tour.*`, `sample.*`).
 *
 * Provides:
 *  - dialogs `DIALOG.firstRun` (modal; asked by the Library on the first launch), `DIALOG.tour` and `DIALOG.help`
 *    (both non-modal surfaces over the live editor);
 *  - commands `help.open` (F1; toggles the panel, also from inside it: a second F1 binding for `dialog &&
 *    help.focused`, since the keyboard router applies only dialog bindings while focus is in a sheet),
 *    `help.forRpeUsers`, `help.glossary`, `help.showMe`, `help.welcome` (the first-run question again; choosing RPE
 *    then opens the "For RPE users" article, see FirstRunDialog.tsx), `tour.start` (closes the Help panel, so focus
 *    is not left in a sheet while the tour asks for editor keys; offers the sample when no chart is open),
 *    `tour.stop`, `sample.open` (builds the sample, opens it and starts the tour);
 *  - the help topics (helpTopics.ts) and the `?` palette provider (topics of every module plus the glossary);
 *  - when-atoms `tour.active` and `help.focused`.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { CommandContext, CommandDef, FeatureModule, KeyBinding, MenuPlacement } from '../../app/registry/types';
import { applyFirstRunAnswer } from './firstRun';
import { FirstRunDialog } from './FirstRunDialog';
import { HelpPanel } from './HelpPanel';
import { HELP_TOPICS, TOPIC } from './helpTopics';
import { m } from './onboarding.messages';
import { helpHasFocus, helpOpen, requestShowMe, tourActive } from './onboardingState';
import { helpPaletteProvider } from './paletteHelp';
import { openSampleProject } from './sampleOpen';
import { Tour } from './Tour';

/** `{ topic }` of help commands (JSON args from keys, the palette or other modules). */
export function topicArg(args: unknown): string | null {
  if (typeof args !== 'object' || args === null || Array.isArray(args)) return null;
  const topic = (args as Readonly<Record<string, unknown>>).topic;
  return typeof topic === 'string' && topic.trim() !== '' ? topic.trim() : null;
}

function helpMenu(group: string, order: number): readonly MenuPlacement[] {
  return [{ menu: 'app.help', group, order }];
}

async function startTour(ctx: CommandContext): Promise<void> {
  if (helpOpen.get()) ctx.ui.closeDialog(DIALOG.help);
  if (ctx.store === null) {
    const ok = await ctx.ui.confirm({
      title: { key: m.tourNeedsChartTitle },
      body: { key: m.tourNeedsChartBody },
      confirm: { key: m.tourOpenSample },
    });
    if (ok) await openSampleProject(ctx);
    return;
  }
  void ctx.ui.openDialog(DIALOG.tour, {});
}

export const ONBOARDING_COMMANDS: readonly CommandDef[] = [
  {
    id: 'help.open',
    title: m.cmdHelpOpen,
    category: 'Help',
    icon: 'LifeBuoy',
    aliases: ['help', 'manual', 'guide', 'how to', 'documentation', '帮助', '教程'],
    level: 'guided',
    hint: 2,
    menus: helpMenu('help', 10),
    run(ctx, args) {
      const topic = topicArg(args);
      if (topic === null && helpOpen.get()) {
        ctx.ui.closeDialog(DIALOG.help);
        return;
      }
      void ctx.ui.openDialog(DIALOG.help, topic === null ? {} : { topic });
    },
  },
  {
    id: 'help.forRpeUsers',
    title: m.cmdHelpRpe,
    category: 'Help',
    icon: 'Repeat',
    aliases: ['rpe', 're:phiedit', 'migrate', 'coming from rpe', 'rpe keymap', 'rpe 用户'],
    level: 'guided',
    menus: helpMenu('help', 20),
    run(ctx) {
      void ctx.ui.openDialog(DIALOG.help, { topic: TOPIC.rpeUsers });
    },
  },
  {
    id: 'help.glossary',
    title: m.cmdHelpGlossary,
    category: 'Help',
    icon: 'Languages',
    aliases: ['glossary', 'terms', 'vocabulary', 'words', '术语'],
    level: 'guided',
    menus: helpMenu('help', 30),
    run(ctx) {
      void ctx.ui.openDialog(DIALOG.help, { topic: TOPIC.glossary });
    },
  },
  {
    id: 'help.showMe',
    title: m.cmdHelpShowMe,
    category: 'Help',
    icon: 'Eye',
    aliases: ['show me', 'where is', 'highlight'],
    level: 'guided',
    args: {
      kind: 'choice',
      label: { key: m.argTopic },
      options: HELP_TOPICS.filter((t) => t.showMe !== undefined).map((t) => ({ value: t.id, label: { key: t.title } })),
    },
    run(ctx, args) {
      const topic = topicArg(args) ?? (typeof args === 'string' ? args : null);
      const def = topic === null ? undefined : ctx.registry.helpTopics().find((t) => t.id === topic);
      if (topic === null || def === undefined) {
        ctx.ui.toast({ key: m.showMeUnknown, params: { topic: topic ?? '' } }, { kind: 'warning' });
        return;
      }
      if (def.showMe !== undefined) requestShowMe(def.id);
      void ctx.ui.openDialog(DIALOG.help, { topic: def.id });
    },
  },
  {
    id: 'help.welcome',
    title: m.cmdHelpWelcome,
    category: 'Help',
    icon: 'Hand',
    aliases: ['welcome', 'first run', 'experience level', 'language', 'start over', '欢迎'],
    level: 'guided',
    menus: helpMenu('start', 30),
    async run(ctx) {
      const answer = await ctx.ui.openDialog(DIALOG.firstRun, null);
      if (answer === null) return;
      applyFirstRunAnswer(ctx, answer);
      ctx.ui.toast({ key: m.welcomeApplied }, { kind: 'success' });
    },
  },
  {
    id: 'tour.start',
    title: m.cmdTourStart,
    category: 'Help',
    icon: 'Footprints',
    aliases: ['tour', 'tutorial', 'walkthrough', 'introduction', 'onboarding', '教学', '引导'],
    level: 'guided',
    menus: helpMenu('start', 10),
    async run(ctx) {
      await startTour(ctx);
    },
  },
  {
    id: 'tour.stop',
    title: m.cmdTourStop,
    category: 'Help',
    icon: 'CircleStop',
    aliases: ['end tour', 'skip tour', 'close tour'],
    level: 'guided',
    enabled: () => (tourActive.get() ? true : { key: m.tourNotRunning }),
    run(ctx) {
      if (!ctx.settings.value.general.tourDone) ctx.settings.set('general', 'tourDone', true);
      ctx.ui.closeDialog(DIALOG.tour);
    },
  },
  {
    id: 'sample.open',
    title: m.cmdSampleOpen,
    category: 'Help',
    icon: 'GraduationCap',
    aliases: ['sample', 'example', 'demo', 'learn', 'try', '示例', '样例'],
    level: 'guided',
    menus: helpMenu('start', 20),
    async run(ctx) {
      await openSampleProject(ctx);
    },
  },
];

/** F1 opens / closes Help; the second binding reaches it while focus is inside the panel (a sheet). */
const F1: readonly KeyBinding[] = [
  { chord: 'F1', command: 'help.open' },
  { chord: 'F1', command: 'help.open', when: 'dialog && help.focused' },
];

export default {
  id: 'onboarding',
  commands: ONBOARDING_COMMANDS,
  keybindings: { default: F1, rpe: F1 },
  dialogs: [
    { id: DIALOG.firstRun, kind: 'modal', component: FirstRunDialog },
    { id: DIALOG.tour, kind: 'sheet', component: Tour },
    { id: DIALOG.help, kind: 'sheet', component: HelpPanel },
  ],
  helpTopics: HELP_TOPICS,
  paletteProviders: (s) => [helpPaletteProvider(s)],
  whenAtoms: () => ({ 'tour.active': () => tourActive.get(), 'help.focused': () => helpHasFocus() }),
} satisfies FeatureModule;
